// The install walkthrough on /download rehearses what the user is about to
// see on their own desktop, beat by beat: the browser's download bubble, the
// real installer for their OS, and the first-run sign-in. React advances one
// phase at a time from these tables; CSS transitions own the motion between
// phases. Windows has no drag-to-Applications and no Gatekeeper prompt, so the
// two timelines are authored separately rather than parameterized.

export type DesktopOs = "mac" | "windows";
export type WalkthroughStep = 1 | 2 | 3;

export type WalkthroughPhase =
  // Step 1 — the browser shows the file it just downloaded.
  | "download.progress"
  | "download.done"
  | "download.aim"
  | "download.click"
  // Step 2 (Mac) — the DMG window: drag 1Transcribe onto Applications.
  | "install.window"
  | "install.aim"
  | "install.grab"
  | "install.drag"
  | "install.hover"
  | "install.drop"
  | "install.copying"
  | "install.copied"
  // Step 2 (Windows) — the one-click setup window installs on its own.
  | "install.setup"
  | "install.installing"
  | "install.installed"
  // Step 3 — first launch. macOS asks once before opening a downloaded app.
  | "open.gatekeeper"
  | "open.gatekeeper.aim"
  | "open.gatekeeper.click"
  | "open.app"
  | "open.signIn.aim"
  | "open.signIn.click"
  | "open.connected";

type Beat = readonly [WalkthroughStep, WalkthroughPhase, number];

const downloadBeats: readonly Beat[] = [
  [1, "download.progress", 1700],
  [1, "download.done", 900],
  [1, "download.aim", 900],
  [1, "download.click", 500],
];

const firstRunBeats: readonly Beat[] = [
  [3, "open.app", 1100],
  [3, "open.signIn.aim", 800],
  [3, "open.signIn.click", 500],
  [3, "open.connected", 2600],
];

const beatsByOs: Record<DesktopOs, readonly Beat[]> = {
  mac: [
    ...downloadBeats,
    [2, "install.window", 1000],
    [2, "install.aim", 800],
    [2, "install.grab", 350],
    [2, "install.drag", 1400],
    [2, "install.hover", 450],
    [2, "install.drop", 450],
    [2, "install.copying", 1300],
    [2, "install.copied", 700],
    [3, "open.gatekeeper", 1000],
    [3, "open.gatekeeper.aim", 800],
    [3, "open.gatekeeper.click", 450],
    ...firstRunBeats,
  ],
  windows: [
    ...downloadBeats,
    [2, "install.setup", 1000],
    [2, "install.installing", 2600],
    [2, "install.installed", 800],
    ...firstRunBeats,
  ],
};

export type WalkthroughFrame = {
  step: WalkthroughStep;
  phase: WalkthroughPhase;
  /** Milliseconds until the next phase starts. */
  next: number;
  /** 0..1 progress through the current step at the start and end of this
   *  phase, so the segmented indicator can tween exactly one phase at a time. */
  stepProgress: number;
  stepProgressEnd: number;
};

export const walkthroughDuration = (os: DesktopOs): number =>
  beatsByOs[os].reduce((total, [, , hold]) => total + hold, 0);

export const walkthroughStepStart = (
  os: DesktopOs,
  step: WalkthroughStep,
): number => {
  let at = 0;
  for (const [beatStep, , hold] of beatsByOs[os]) {
    if (beatStep === step) return at;
    at += hold;
  }
  return 0;
};

const stepDuration = (os: DesktopOs, step: WalkthroughStep): number =>
  beatsByOs[os]
    .filter(([beatStep]) => beatStep === step)
    .reduce((total, [, , hold]) => total + hold, 0);

/** The phase playing at `elapsedMs`; the timeline loops. */
export const walkthroughFrame = (
  os: DesktopOs,
  elapsedMs: number,
): WalkthroughFrame => {
  const duration = walkthroughDuration(os);
  const elapsed =
    ((Math.max(0, elapsedMs) % duration) + duration) % duration;
  let at = 0;
  for (const [step, phase, hold] of beatsByOs[os]) {
    if (elapsed < at + hold) {
      const stepStart = walkthroughStepStart(os, step);
      const length = stepDuration(os, step);
      return {
        step,
        phase,
        next: at + hold - elapsed,
        stepProgress: (elapsed - stepStart) / length,
        stepProgressEnd: (at + hold - stepStart) / length,
      };
    }
    at += hold;
  }
  throw new Error("walkthrough timeline has a gap");
};

/** The resting frame of a step, shown when motion is reduced. */
export const walkthroughStillFrame = (
  os: DesktopOs,
  step: WalkthroughStep,
): WalkthroughFrame =>
  walkthroughFrame(
    os,
    walkthroughStepStart(os, step) + stepDuration(os, step) - 1,
  );
